import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * Wrapper for react-leaflet MapContainer that prevents the
 * "Map container is already initialized" error in React 19 strict mode.
 *
 * React 19 strict mode mounts → unmounts → remounts components, but
 * Leaflet attaches to the DOM container on first mount and doesn't
 * clean up. This wrapper delays rendering the children (MapContainer)
 * until after the strict-mode cleanup cycle completes, and forces a
 * fresh DOM node on each mount.
 */
export function SafeMap({
  children,
  height,
  className,
}: {
  children: ReactNode;
  height?: number;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Small delay to let React 19 strict mode complete its unmount/remount cycle
    const timer = requestAnimationFrame(() => setReady(true));
    return () => {
      cancelAnimationFrame(timer);
      setReady(false);
      // Force-clean the container so Leaflet doesn't see a stale instance
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, []);

  const style: React.CSSProperties = { width: '100%' };
  if (height !== undefined) style.height = height;
  return (
    <div
      ref={containerRef}
      style={style}
      className={className ?? (height === undefined ? 'h-[500px]' : undefined)}
    >
      {ready ? children : null}
    </div>
  );
}
